import { formatDepositInstant, type DepositMarker } from '@/lib/deposit-events'
import type { ReviewMarker } from '@/lib/review-events'
import { supplyMarkerDetail, type SupplyMarker } from '@/lib/supply-events'
import { eventBlockMeta, type EventBlockSpec } from './event-block-meta'

/**
 * Each layer's data as event blocks. What a block looks like and says is
 * `eventBlockMeta`'s; here is only what each layer adds to it: the time or
 * quantity, what a click opens, and the attributes its tests read.
 */

/**
 * The deposit events of one day. Each opens the stay sheet scrolled to the
 * deposit timeline, which is where every event - the renewals the grid folds
 * away included - can be read in full.
 */
export function depositEventBlocks(
  markers: DepositMarker[],
  timeZone: string,
  onOpen: (reservationId: string) => void
): EventBlockSpec[] {
  return markers.map(marker => ({
    key: marker.key,
    layer: 'deposit',
    meta: eventBlockMeta({ layer: 'deposit', type: marker.type, planned: marker.planned }),
    detail: formatDepositInstant(marker.at, timeZone),
    action: 'open deposit timeline',
    data: {
      'data-event-type': marker.type,
      'data-planned': marker.planned ? 'true' : 'false',
    },
    onOpen: () => onOpen(marker.reservationId),
  }))
}

/** The supply runouts and deliveries of one day. Each opens its category's sheet. */
export function supplyEventBlocks(
  markers: SupplyMarker[],
  onOpen: (marker: SupplyMarker) => void
): EventBlockSpec[] {
  return markers.map(marker => ({
    key: marker.key,
    layer: 'supply',
    meta: eventBlockMeta({
      layer: 'supply',
      kind: marker.kind,
      category: marker.label,
      confidence: marker.confidence,
    }),
    detail: supplyMarkerDetail(marker),
    action: 'open supply details',
    data: { 'data-supply-kind': marker.kind, 'data-category': marker.category },
    onOpen: () => onOpen(marker),
  }))
}

/**
 * The review events of one day: the window closing and the reviews that landed.
 * Each opens the stay sheet scrolled to its review section, which says the rest
 * (who reviewed, what the chase has done) and links to the drafts.
 */
export function reviewEventBlocks(
  markers: ReviewMarker[],
  onOpen: (reservationId: string) => void
): EventBlockSpec[] {
  return markers.map(marker => ({
    key: marker.key,
    layer: 'review',
    meta: eventBlockMeta({
      layer: 'review',
      kind: marker.kind,
      planned: marker.planned,
      owed: marker.owed,
    }),
    detail: marker.detail,
    action: 'open review details',
    data: {
      'data-review-kind': marker.kind,
      'data-reservation-id': marker.reservationId,
    },
    onOpen: () => onOpen(marker.reservationId),
  }))
}
